import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Heart, Sparkles, Moon } from "lucide-react";
import { http, BRAND } from "../lib/api";
import ProductCard from "../components/ProductCard";

function HeroStars() {
  const stars = Array.from({ length: 24 }, (_, i) => ({
    top: `${Math.random() * 90}%`,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 3}s`,
  }));
  return (
    <>{stars.map((s, i) => <span key={i} className="star" style={{ top: s.top, left: s.left, animationDelay: s.delay }} />)}</>
  );
}

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    http.get("/products?featured=true").then((r) => setProducts(r.data)).catch(() => {});
    http.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: "var(--vdz-navy)", color: "var(--vdz-cream)" }}>
        <HeroStars />
        <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full" style={{ background: "radial-gradient(circle, rgba(201,162,74,0.25) 0%, transparent 60%)" }} />
        <div className="container-vdz relative py-20 md:py-28 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="fade-in-up">
            <div className="eyebrow mb-5">Alina Stancu • Carte de colorat</div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              Un <em className="italic text-[color:var(--vdz-gold-pale)]">vis de zi</em>
              <br />colorat de tine.
            </h1>
            <div className="mt-6 font-serif-accent uppercase tracking-[0.28em] text-sm md:text-base gold-text">
              {BRAND.slogan}
            </div>
            <p className="mt-8 text-base md:text-lg opacity-85 max-w-md leading-relaxed">
              {BRAND.tagline} O carte de colorat elegantă, feminină, atmosferică — pentru femeile care au nevoie de un moment doar al lor.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/carti" className="btn-gold" data-testid="hero-cta-discover-button">
                Descoperă cărțile
              </Link>
              <Link to="/galerie" className="btn-ghost" data-testid="hero-cta-gallery-button">
                Vezi galeria
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-6 text-xs opacity-70">
              <div className="flex items-center gap-2"><Sparkles size={14} /> Hârtie premium</div>
              <div className="flex items-center gap-2"><Moon size={14} /> Ilustrații originale</div>
              <div className="flex items-center gap-2"><Heart size={14} /> Creat în România</div>
            </div>
          </div>

          <div className="relative flex justify-center lg:justify-end">
            <div className="relative float-slow">
              <div className="absolute -inset-6 blur-3xl opacity-50" style={{ background: "radial-gradient(circle, rgba(201,162,74,0.5) 0%, transparent 70%)" }} />
              <div className="relative gold-frame p-2" style={{ background: "rgba(249,246,240,0.05)" }}>
                <img src={BRAND.cover} alt="VIS DE ZI – Carte de colorat"
                     className="relative w-[280px] sm:w-[320px] md:w-[380px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]"
                     data-testid="hero-cover-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONCEPT */}
      <section className="py-20 md:py-28">
        <div className="container-vdz">
          <div className="max-w-3xl mx-auto text-center">
            <div className="eyebrow mb-5">Conceptul Vis de Zi</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--vdz-navy)" }}>
              O oră în care încetinești,<br />te relaxezi și visezi.
            </h2>
            <p className="mt-8 text-lg opacity-80 leading-relaxed max-w-xl mx-auto">
              VIS DE ZI este un brand creativ românesc construit în jurul coloratului pentru adulți —
              un ritual elegant de relaxare, imaginație și timp dedicat ție.
              Fiecare pagină este o invitație la un moment de respirație frumoasă.
            </p>
            <div className="gold-divider mt-10"><span className="eyebrow">Alina Stancu</span></div>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-16 md:py-24" style={{ background: "var(--vdz-cream-warm)" }}>
        <div className="container-vdz">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="eyebrow mb-3">Colecția</div>
              <h2 className="font-serif-display text-4xl md:text-5xl" style={{ color: "var(--vdz-navy)" }}>Cărțile Vis de Zi</h2>
            </div>
            <Link to="/carti" className="btn-dark" data-testid="home-view-all-button">Vezi toate →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="home-products-grid">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* WHY COLORING */}
      <section className="py-20 md:py-28">
        <div className="container-vdz grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <div className="eyebrow mb-5">De ce coloratul?</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--vdz-navy)" }}>
              Un ritual blând de reconectare cu tine însăți.
            </h2>
            <div className="mt-8 space-y-6">
              {[
                { t: "Liniște prin creativitate", d: "Coloratul este o formă de meditație activă — ochii, mâinile și mintea se calmează împreună." },
                { t: "Timp doar pentru tine", d: "O oră în care închizi notificările, aprinzi o lumânare și visezi la paginile tale frumoase." },
                { t: "Frumusețe pe hârtie", d: "Fiecare ilustrație este concepută ca o operă pe care o aduci la viață cu culorile tale." },
              ].map((b, i) => (
                <div key={i} className="flex gap-5">
                  <div className="shrink-0 w-10 h-10 flex items-center justify-center gold-frame" style={{ background: "var(--vdz-cream)" }}>
                    <Star size={16} className="gold-text" fill="currentColor" style={{ color: "var(--vdz-gold)" }} />
                  </div>
                  <div>
                    <h3 className="font-serif-display text-xl" style={{ color: "var(--vdz-navy)" }}>{b.t}</h3>
                    <p className="text-sm md:text-base opacity-75 mt-1 leading-relaxed">{b.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="gold-frame">
              <img src={BRAND.mockup} alt="Vis de Zi atmosferă" className="w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="py-16 md:py-24" style={{ background: "var(--vdz-navy)", color: "var(--vdz-cream)" }}>
        <div className="container-vdz">
          <div className="text-center max-w-xl mx-auto mb-14">
            <div className="eyebrow mb-3">Pagini din carte</div>
            <h2 className="font-serif-display text-4xl md:text-5xl">Pentru imaginația ta.</h2>
            <p className="mt-4 opacity-75">Ilustrații elegante, feminine, fantastice — gata să fie colorate.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[BRAND.pageColoredPink, BRAND.pageGeisha, BRAND.pagePolkaDot, BRAND.pagePeonies].map((src, i) => (
              <div key={i} className="aspect-[3/4] overflow-hidden gold-frame" style={{ background: "rgba(255,255,255,0.03)" }}>
                <img src={src} alt="Pagină Vis de Zi" className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/galerie" className="btn-ghost" data-testid="home-gallery-link">Vezi întreaga galerie</Link>
          </div>
        </div>
      </section>

      {/* SEASONAL */}
      <section className="py-20 md:py-28">
        <div className="container-vdz">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="eyebrow mb-3">Colecții sezoniere</div>
            <h2 className="font-serif-display text-4xl md:text-5xl" style={{ color: "var(--vdz-navy)" }}>Un vis pentru fiecare anotimp.</h2>
            <p className="mt-4 opacity-75">Ediții speciale limitate, create pentru fiecare sezon al anului.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { name: "Halloween", desc: "Disponibilă acum", color: "var(--vdz-burnt-orange)", available: true },
              { name: "Primăvara", desc: "În curând", color: "#B5C9A8" },
              { name: "Vara", desc: "În curând", color: "#E6C875" },
              { name: "Iarna", desc: "În curând", color: "#C7D2E3" },
            ].map((c) => (
              <div key={c.name} className="p-8 border text-center transition-all hover:border-[color:var(--vdz-gold)]" style={{ borderColor: "var(--vdz-cream-border)", background: "white" }}>
                <div className="w-12 h-12 mx-auto rounded-full" style={{ background: c.color }} />
                <h3 className="font-serif-display text-2xl mt-5" style={{ color: "var(--vdz-navy)" }}>{c.name}</h3>
                <p className="text-xs mt-2 opacity-70 uppercase tracking-[0.18em] font-serif-accent">{c.desc}</p>
                {c.available && (
                  <Link to="/carti/vis-de-zi-halloween" className="mt-5 inline-block text-xs gold-text font-serif-accent uppercase tracking-[0.2em]">
                    Vezi cartea →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="py-20 md:py-28" style={{ background: "var(--vdz-cream-warm)" }}>
          <div className="container-vdz">
            <div className="text-center max-w-xl mx-auto mb-14">
              <div className="eyebrow mb-3">Visătoarele noastre</div>
              <h2 className="font-serif-display text-4xl md:text-5xl" style={{ color: "var(--vdz-navy)" }}>Ce spun cititoarele.</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6" data-testid="testimonials-grid">
              {testimonials.slice(0, 3).map((t) => (
                <div key={t.id} className="p-7 bg-white border" style={{ borderColor: "var(--vdz-cream-border)" }}>
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} size={14} fill="currentColor" style={{ color: "var(--vdz-gold)" }} />
                    ))}
                  </div>
                  <p className="font-serif-display text-lg leading-relaxed italic" style={{ color: "var(--vdz-navy)" }}>"{t.text}"</p>
                  <div className="mt-5 pt-5 border-t text-sm" style={{ borderColor: "var(--vdz-cream-border)" }}>
                    <span className="font-semibold">{t.name}</span>
                    {t.city && <span className="opacity-60"> · {t.city}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
